/**
 * probeVideoMeta — parse an ISO BMFF (MP4 / MOV / fragmented MP4) container
 * to pull metadata the HTMLVideoElement won't expose: codec, fps, bitrate.
 *
 * QuickTime / iPhone MOV captures commonly put the `moov` atom at the END
 * of the file, after a large `mdat`. To handle that without loading the
 * whole file at once, we stream the file into mp4box.js in chunks and stop
 * as soon as it emits `onReady` (which fires the moment moov is parsed).
 *
 * Returns null when the file isn't ISO BMFF or we exhaust the file without
 * mp4box reporting a video track.
 */
import * as MP4Box from 'mp4box';

const CHUNK_SIZE = 4 * 1024 * 1024; // 4 MB

function readableCodec(codec) {
  if (!codec) return null;
  const c = codec.toLowerCase();
  if (c.startsWith('avc1') || c.startsWith('avc3')) return `H.264 (${codec})`;
  if (c.startsWith('hvc1') || c.startsWith('hev1')) return `H.265 / HEVC (${codec})`;
  if (c.startsWith('av01')) return `AV1 (${codec})`;
  if (c.startsWith('vp09')) return `VP9 (${codec})`;
  if (c.startsWith('vp08')) return `VP8 (${codec})`;
  return codec;
}

export async function probeVideoMeta(file, { timeoutMs = 20000 } = {}) {
  if (!file || typeof file.slice !== 'function') return null;

  return new Promise((resolve) => {
    let settled = false;
    const finish = (v) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(v);
    };
    const timer = setTimeout(() => finish(null), timeoutMs);

    const mp4 = MP4Box.createFile();
    mp4.onError = () => finish(null);
    mp4.onReady = (info) => {
      const vTrack = info.videoTracks?.[0];
      if (!vTrack) return finish(null);
      const seconds = vTrack.duration / vTrack.timescale;
      const fps = seconds > 0 ? vTrack.nb_samples / seconds : null;
      const bitrate = vTrack.bitrate || info.bitrate || null;
      finish({
        codec: readableCodec(vTrack.codec),
        fps: fps && Number.isFinite(fps) ? fps : null,
        bitrate,
        brand: info.brands?.[0] || null,
      });
    };

    (async () => {
      try {
        let offset = 0;
        while (offset < file.size && !settled) {
          const end = Math.min(offset + CHUNK_SIZE, file.size);
          // eslint-disable-next-line no-await-in-loop
          const buf = await file.slice(offset, end).arrayBuffer();
          if (settled) return;
          buf.fileStart = offset;
          mp4.appendBuffer(buf);
          offset = end;
        }
        if (!settled) {
          mp4.flush();
          // Give mp4box a tick to fire onReady from the final flush; if it
          // hasn't by the timeout, we resolve null.
        }
      } catch {
        finish(null);
      }
    })();
  });
}

export default probeVideoMeta;
